import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';

// ── Types ────────────────────────────────────────────────────────

interface ProgramSummary {
  program_id: string;
  program_name: string;
  client_code: string;
  coverholder_code: string;
  active: boolean;
  modules_enabled: string[];
  updated_at: string;
}

interface ProgramConfig {
  program_id: string;
  program_name: string;
  client_code: string;
  coverholder_code: string;
  active: boolean;
  bdx_coveholder_id: string;
  sla_org_id: string;
  financial: {
    deductible_basis: string;
    deductible_sequencing: string;
    deductible_applies_per: string;
    net_basis_label: string;
    tcm_fee_rate: number;
    tcm_fee_source: string;
  };
  loss_run: {
    enabled: boolean;
    open_status_filter: string[];
    group_by: string;
    include_lae: boolean;
    show_deductible_column: boolean;
    custom_column_overrides: Record<string, string>;
  };
  bdx: {
    enabled: boolean;
    coro_split: boolean;
    client_claim_num_prefix: string;
    custom_field_overrides: Record<string, string>;
    tcm_fee_rate_override: number | null;
  };
  sla: {
    enabled: boolean;
    metric_overrides: Record<string, any>;
    threshold_overrides: Record<string, any>;
  };
  created_at: string;
  updated_at: string;
  created_by: string;
}

// ── Helpers ──────────────────────────────────────────────────────

const DEDUCTIBLE_BASIS_OPTIONS = [
  { value: 'combined', label: 'Combined Loss + LAE' },
  { value: 'loss_only', label: 'Indemnity Only' },
  { value: 'lae_only', label: 'LAE Only' },
];

const DEDUCTIBLE_PER_OPTIONS = [
  { value: 'claim', label: 'Per Claim' },
  { value: 'occurrence', label: 'Per Occurrence' },
];

const NET_BASIS_OPTIONS = [
  { value: 'net_to_underwriters', label: 'Net to Underwriters' },
  { value: 'net_to_client', label: 'Net to Client' },
];

const FEE_SOURCE_OPTIONS = [
  { value: 'from_db', label: 'From Database' },
  { value: 'fixed_per_claim', label: 'Fixed Per Claim' },
  { value: 'percentage', label: 'Percentage of Loss' },
];

const GROUP_BY_OPTIONS = [
  { value: 'contract_umr', label: 'Contract UMR' },
  { value: 'coverholder', label: 'Coverholder' },
  { value: 'client', label: 'Client' },
];

const STATUS_FILTER_OPTIONS = [
  { value: 'O', label: 'Open' },
  { value: 'R', label: 'Reopened' },
  { value: 'C', label: 'Closed' },
];

function formatDate(iso: string) {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleDateString(); }
  catch { return iso; }
}

// ── Main Component ───────────────────────────────────────────────

export default function ProgramAdmin() {
  const [programs, setPrograms] = useState<ProgramSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showWizard, setShowWizard] = useState(false);
  const [error, setError] = useState('');

  const loadPrograms = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string> = {};
      if (search) params.search = search;
      const data = await api.getPrograms(params);
      setPrograms(data.programs || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load programs');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { loadPrograms(); }, [loadPrograms]);

  if (selectedId) {
    return (
      <ProgramEditor
        programId={selectedId}
        onBack={() => { setSelectedId(null); loadPrograms(); }}
      />
    );
  }

  if (showWizard) {
    return (
      <NewProgramWizard
        onComplete={(id) => { setShowWizard(false); setSelectedId(id); }}
        onCancel={() => setShowWizard(false)}
      />
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Program Configuration</h1>
          <p className="text-sm text-slate-500 mt-1">
            Per-program rules for Loss Run, BDX, and SLA generation
          </p>
        </div>
        <button
          onClick={() => setShowWizard(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
        >
          New Program
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by name, client code, or ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-md px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">Loading programs...</div>
      ) : programs.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          No programs found. Create one to get started.
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Program Name</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Client Code</th>
                <th className="text-center px-4 py-3 font-medium text-slate-600">Active</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Modules</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Last Updated</th>
                <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {programs.map((p) => (
                <tr
                  key={p.program_id}
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => setSelectedId(p.program_id)}
                >
                  <td className="px-4 py-3 font-medium text-slate-800">{p.program_name}</td>
                  <td className="px-4 py-3 text-slate-600 font-mono text-xs">{p.client_code}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block w-2 h-2 rounded-full ${p.active ? 'bg-green-500' : 'bg-slate-300'}`} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {p.modules_enabled.map((m) => (
                        <span key={m} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                          {m === 'loss_run' ? 'LR' : m === 'bdx' ? 'BDX' : 'SLA'}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{formatDate(p.updated_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button className="text-blue-600 hover:text-blue-800 text-xs font-medium">
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Program Editor ───────────────────────────────────────────────

function ProgramEditor({ programId, onBack }: { programId: string; onBack: () => void }) {
  const [config, setConfig] = useState<ProgramConfig | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState<Set<number>>(new Set());
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);

  useEffect(() => {
    api.getProgram(programId)
      .then((data) => { setConfig(data); setLoading(false); })
      .catch((err) => { setError(err.message); setLoading(false); });
  }, [programId]);

  const markDirty = () => {
    setDirty((prev) => new Set(prev).add(activeTab));
    setSuccess('');
  };

  const updateConfig = (path: string, value: any) => {
    if (!config) return;
    const parts = path.split('.');
    const updated = { ...config };
    let obj: any = updated;
    for (let i = 0; i < parts.length - 1; i++) {
      obj[parts[i]] = { ...obj[parts[i]] };
      obj = obj[parts[i]];
    }
    obj[parts[parts.length - 1]] = value;
    setConfig(updated);
    markDirty();
  };

  const saveTab = async () => {
    if (!config) return;
    setSaving(true);
    setError('');
    try {
      const tabSections: Record<number, any> = {
        0: {
          program_name: config.program_name,
          client_code: config.client_code,
          coverholder_code: config.coverholder_code,
          active: config.active,
          bdx_coveholder_id: config.bdx_coveholder_id,
          sla_org_id: config.sla_org_id,
        },
        1: { financial: config.financial },
        2: { loss_run: config.loss_run },
        3: { bdx: config.bdx },
        4: { sla: config.sla },
      };
      await api.patchProgram(programId, tabSections[activeTab]);
      setDirty((prev) => { const n = new Set(prev); n.delete(activeTab); return n; });
      setSuccess('Saved successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    try {
      await api.deleteProgram(programId);
      setShowDeactivateModal(false);
      onBack();
    } catch (err: any) {
      setError(err.message || 'Deactivation failed');
    }
  };

  if (loading) return <div className="p-6 text-center text-slate-500">Loading program...</div>;
  if (!config) return <div className="p-6 text-center text-red-500">{error || 'Program not found'}</div>;

  const tabs = ['Identity', 'Financial Rules', 'Loss Run', 'BDX', 'SLA'];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="text-slate-500 hover:text-slate-700 text-sm">
          &larr; Back
        </button>
        <h1 className="text-xl font-bold text-slate-800">{config.program_name || config.program_id}</h1>
        {!config.active && (
          <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium">Inactive</span>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{success}</div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-slate-200 mb-6">
        {tabs.map((label, i) => (
          <button
            key={i}
            onClick={() => setActiveTab(i)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === i
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {label}
            {dirty.has(i) && <span className="ml-1 text-amber-500">*</span>}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="bg-white rounded-lg border border-slate-200 p-6">
        {activeTab === 0 && (
          <IdentityTab config={config} onChange={updateConfig}
                       onDeactivate={() => setShowDeactivateModal(true)} />
        )}
        {activeTab === 1 && <FinancialTab config={config} onChange={updateConfig} />}
        {activeTab === 2 && <LossRunTab config={config} onChange={updateConfig} />}
        {activeTab === 3 && <BdxTab config={config} onChange={updateConfig} />}
        {activeTab === 4 && <SlaTab config={config} onChange={updateConfig} />}

        {dirty.has(activeTab) && (
          <div className="mt-6 flex items-center gap-3 pt-4 border-t border-slate-200">
            <button
              onClick={saveTab}
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:bg-blue-300"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <span className="text-xs text-slate-400">Unsaved changes on this tab</span>
          </div>
        )}
      </div>

      {/* Deactivation modal */}
      {showDeactivateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-bold text-slate-800 mb-2">Deactivate Program?</h3>
            <p className="text-sm text-slate-600 mb-4">
              This will mark <strong>{config.program_name}</strong> as inactive.
              The engines will fall back to default settings. This can be reversed.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setShowDeactivateModal(false)}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800">Cancel</button>
              <button onClick={handleDeactivate}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium">
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab Components ───────────────────────────────────────────────

function FieldRow({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-4 items-start py-3">
      <div>
        <label className="text-sm font-medium text-slate-700">{label}</label>
        {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
      </div>
      <div className="col-span-2">{children}</div>
    </div>
  );
}

function TextInput({ value, onChange, placeholder, mono }: { value: string; onChange: (v: string) => void; placeholder?: string; mono?: boolean }) {
  return (
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full max-w-sm px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 ${mono ? 'font-mono' : ''}`}
    />
  );
}

function NumberInput({ value, onChange, placeholder }: { value: number | null; onChange: (v: number | null) => void; placeholder?: string }) {
  return (
    <input
      type="number"
      step="any"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value ? parseFloat(e.target.value) : null)}
      placeholder={placeholder}
      className="w-full max-w-xs px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 font-mono"
    />
  );
}

function SelectInput({ value, options, onChange }: { value: string; options: { value: string; label: string }[]; onChange: (v: string) => void }) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="px-3 py-1.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

function Toggle({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label?: string }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer">
      <div className="relative">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only peer" />
        <div className="w-9 h-5 bg-slate-300 rounded-full peer-checked:bg-blue-600 transition-colors"></div>
        <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full peer-checked:translate-x-4 transition-transform"></div>
      </div>
      {label && <span className="text-sm text-slate-700">{label}</span>}
    </label>
  );
}

function IdentityTab({ config, onChange, onDeactivate }: { config: ProgramConfig; onChange: (path: string, value: any) => void; onDeactivate: () => void }) {
  return (
    <div className="divide-y divide-slate-100">
      <FieldRow label="Program ID" hint="Cannot be changed after creation">
        <span className="text-sm font-mono text-slate-600">{config.program_id}</span>
      </FieldRow>
      <FieldRow label="Program Name">
        <TextInput value={config.program_name} onChange={(v) => onChange('program_name', v)} />
      </FieldRow>
      <FieldRow label="Client Code" hint="FileHandler client_code">
        <TextInput value={config.client_code} onChange={(v) => onChange('client_code', v)} mono />
      </FieldRow>
      <FieldRow label="Coverholder Code">
        <TextInput value={config.coverholder_code} onChange={(v) => onChange('coverholder_code', v)} mono />
      </FieldRow>
      <FieldRow label="Active">
        <div className="flex items-center gap-4">
          <Toggle checked={config.active} onChange={(v) => onChange('active', v)} />
          {config.active && (
            <button onClick={onDeactivate} className="text-xs text-red-600 hover:text-red-800">
              Deactivate program...
            </button>
          )}
        </div>
      </FieldRow>
      <FieldRow label="BDX Coverholder ID" hint="FK to bdx-coveholder-configs">
        <TextInput value={config.bdx_coveholder_id} onChange={(v) => onChange('bdx_coveholder_id', v)} mono />
      </FieldRow>
      <FieldRow label="SLA Org ID" hint="FK to sla-org-configs">
        <TextInput value={config.sla_org_id} onChange={(v) => onChange('sla_org_id', v)} mono />
      </FieldRow>
    </div>
  );
}

function FinancialTab({ config, onChange }: { config: ProgramConfig; onChange: (path: string, value: any) => void }) {
  const f = config.financial;
  return (
    <div className="divide-y divide-slate-100">
      <FieldRow label="Deductible Basis">
        <SelectInput value={f.deductible_basis} options={DEDUCTIBLE_BASIS_OPTIONS}
                     onChange={(v) => onChange('financial.deductible_basis', v)} />
      </FieldRow>
      <FieldRow label="Deductible Applies Per">
        <SelectInput value={f.deductible_applies_per} options={DEDUCTIBLE_PER_OPTIONS}
                     onChange={(v) => onChange('financial.deductible_applies_per', v)} />
      </FieldRow>
      <FieldRow label="Net Basis Label">
        <SelectInput value={f.net_basis_label} options={NET_BASIS_OPTIONS}
                     onChange={(v) => onChange('financial.net_basis_label', v)} />
      </FieldRow>
      <FieldRow label="TCM Fee Rate" hint="Leave blank to use system default $160/hr">
        <NumberInput value={f.tcm_fee_rate} onChange={(v) => onChange('financial.tcm_fee_rate', v)} placeholder="160.00" />
      </FieldRow>
      <FieldRow label="TCM Fee Source">
        <SelectInput value={f.tcm_fee_source} options={FEE_SOURCE_OPTIONS}
                     onChange={(v) => onChange('financial.tcm_fee_source', v)} />
      </FieldRow>
      <FieldRow label="Deductible Sequencing" hint="'paid_first' is a future enhancement">
        <span className="text-sm text-slate-500">{f.deductible_sequencing || 'combined_total'}</span>
      </FieldRow>
    </div>
  );
}

function LossRunTab({ config, onChange }: { config: ProgramConfig; onChange: (path: string, value: any) => void }) {
  const lr = config.loss_run;
  const toggleStatus = (val: string) => {
    const current = lr.open_status_filter || [];
    const next = current.includes(val) ? current.filter((v) => v !== val) : [...current, val];
    onChange('loss_run.open_status_filter', next);
  };

  return (
    <div className="divide-y divide-slate-100">
      <FieldRow label="Loss Run Enabled">
        <Toggle checked={lr.enabled} onChange={(v) => onChange('loss_run.enabled', v)} />
      </FieldRow>
      <FieldRow label="Open Status Filter" hint="Which statuses count as 'open'">
        <div className="flex gap-4">
          {STATUS_FILTER_OPTIONS.map((opt) => (
            <label key={opt.value} className="inline-flex items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                checked={(lr.open_status_filter || []).includes(opt.value)}
                onChange={() => toggleStatus(opt.value)}
                className="rounded border-slate-300"
              />
              {opt.label}
              {opt.value === 'C' && (lr.open_status_filter || []).includes('C') && (
                <span className="text-amber-500 text-xs ml-1">(unusual)</span>
              )}
            </label>
          ))}
        </div>
      </FieldRow>
      <FieldRow label="Group By">
        <SelectInput value={lr.group_by} options={GROUP_BY_OPTIONS}
                     onChange={(v) => onChange('loss_run.group_by', v)} />
      </FieldRow>
      <FieldRow label="Include LAE">
        <Toggle checked={lr.include_lae} onChange={(v) => onChange('loss_run.include_lae', v)} />
      </FieldRow>
      <FieldRow label="Show Deductible Column">
        <Toggle checked={lr.show_deductible_column} onChange={(v) => onChange('loss_run.show_deductible_column', v)} />
      </FieldRow>
    </div>
  );
}

function BdxTab({ config, onChange }: { config: ProgramConfig; onChange: (path: string, value: any) => void }) {
  const b = config.bdx;
  return (
    <div className="divide-y divide-slate-100">
      <FieldRow label="BDX Enabled">
        <Toggle checked={b.enabled} onChange={(v) => onChange('bdx.enabled', v)} />
      </FieldRow>
      <FieldRow label="CORO Split">
        <div>
          <Toggle checked={b.coro_split} onChange={(v) => onChange('bdx.coro_split', v)} />
          {b.coro_split && (
            <p className="text-xs text-slate-500 mt-1">CORO claims will go to a separate workbook</p>
          )}
        </div>
      </FieldRow>
      <FieldRow label="Client Claim # Prefix" hint="Leave blank for no prefix (e.g. 'CCL-')">
        <TextInput value={b.client_claim_num_prefix} onChange={(v) => onChange('bdx.client_claim_num_prefix', v)} mono placeholder="e.g. CCL-" />
      </FieldRow>
      <FieldRow label="TCM Fee Rate Override" hint="Overrides financial tab rate for BDX only">
        <NumberInput value={b.tcm_fee_rate_override} onChange={(v) => onChange('bdx.tcm_fee_rate_override', v)} placeholder="Leave blank for default" />
      </FieldRow>
    </div>
  );
}

function SlaTab({ config, onChange }: { config: ProgramConfig; onChange: (path: string, value: any) => void }) {
  const s = config.sla;
  return (
    <div className="divide-y divide-slate-100">
      <FieldRow label="SLA Enabled">
        <Toggle checked={s.enabled} onChange={(v) => onChange('sla.enabled', v)} />
      </FieldRow>
      {s.enabled && (
        <FieldRow label="Metric Overrides" hint="Override specific metric thresholds vs org default">
          <div className="text-sm text-slate-500">
            {Object.keys(s.metric_overrides || {}).length === 0
              ? 'No overrides configured. Edit will be available when SLA org is linked.'
              : (
                <div className="space-y-2">
                  {Object.entries(s.metric_overrides).map(([key, val]: [string, any]) => (
                    <div key={key} className="flex items-center gap-2">
                      <span className="font-mono text-xs">{key}</span>
                      <span className="text-slate-400">&rarr;</span>
                      <span>{JSON.stringify(val)}</span>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </FieldRow>
      )}
    </div>
  );
}

// ── New Program Wizard ──────────────────────────────────────────

function NewProgramWizard({ onComplete, onCancel }: { onComplete: (id: string) => void; onCancel: () => void }) {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    program_id: '',
    program_name: '',
    client_code: '',
    coverholder_code: '',
    bdx_coveholder_id: '',
    sla_org_id: '',
    // Financial
    deductible_basis: 'combined',
    deductible_applies_per: 'claim',
    net_basis_label: 'net_to_underwriters',
    tcm_fee_rate: 160,
    tcm_fee_source: 'from_db',
    // Modules
    loss_run_enabled: true,
    bdx_enabled: true,
    sla_enabled: false,
    coro_split: false,
    client_claim_num_prefix: '',
    open_status_filter: ['O', 'R'] as string[],
    group_by: 'contract_umr',
  });

  const update = (key: string, value: any) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleCreate = async () => {
    if (!form.program_id || !form.program_name) {
      setError('Program ID and Name are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api.createProgram({
        program_id: form.program_id,
        program_name: form.program_name,
        client_code: form.client_code,
        coverholder_code: form.coverholder_code,
        bdx_coveholder_id: form.bdx_coveholder_id || form.program_id,
        sla_org_id: form.sla_org_id,
        active: true,
        financial: {
          deductible_basis: form.deductible_basis,
          deductible_sequencing: 'combined_total',
          deductible_applies_per: form.deductible_applies_per,
          net_basis_label: form.net_basis_label,
          tcm_fee_rate: form.tcm_fee_rate,
          tcm_fee_source: form.tcm_fee_source,
        },
        loss_run: {
          enabled: form.loss_run_enabled,
          open_status_filter: form.open_status_filter,
          group_by: form.group_by,
          include_lae: true,
          show_deductible_column: true,
        },
        bdx: {
          enabled: form.bdx_enabled,
          coro_split: form.coro_split,
          client_claim_num_prefix: form.client_claim_num_prefix,
        },
        sla: {
          enabled: form.sla_enabled,
        },
      });
      onComplete(form.program_id);
    } catch (err: any) {
      setError(err.message || 'Failed to create program');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onCancel} className="text-slate-500 hover:text-slate-700 text-sm">&larr; Cancel</button>
        <h1 className="text-xl font-bold text-slate-800">New Program</h1>
      </div>

      {/* Step indicator */}
      <div className="flex gap-2 mb-6">
        {['Identity', 'Financial Rules', 'Module Settings'].map((label, i) => (
          <div key={i} className={`flex-1 text-center py-2 text-xs font-medium rounded ${
            i === step ? 'bg-blue-600 text-white' : i < step ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-400'
          }`}>
            {label}
          </div>
        ))}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 p-6">
        {step === 0 && (
          <div className="space-y-4">
            <FieldRow label="Program ID" hint="Lowercase, underscores. Cannot change later.">
              <TextInput value={form.program_id} onChange={(v) => update('program_id', v.toLowerCase().replace(/[^a-z0-9_]/g, '_'))} mono placeholder="e.g. concord_health" />
            </FieldRow>
            <FieldRow label="Program Name">
              <TextInput value={form.program_name} onChange={(v) => update('program_name', v)} placeholder="e.g. Concord Health Systems" />
            </FieldRow>
            <FieldRow label="Client Code" hint="FileHandler client_code">
              <TextInput value={form.client_code} onChange={(v) => update('client_code', v)} mono placeholder="e.g. 00060" />
            </FieldRow>
            <FieldRow label="Coverholder Code">
              <TextInput value={form.coverholder_code} onChange={(v) => update('coverholder_code', v)} mono />
            </FieldRow>
            <FieldRow label="Enable Modules">
              <div className="flex gap-4">
                <Toggle checked={form.loss_run_enabled} onChange={(v) => update('loss_run_enabled', v)} label="Loss Run" />
                <Toggle checked={form.bdx_enabled} onChange={(v) => update('bdx_enabled', v)} label="BDX" />
                <Toggle checked={form.sla_enabled} onChange={(v) => update('sla_enabled', v)} label="SLA" />
              </div>
            </FieldRow>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <FieldRow label="Deductible Basis">
              <SelectInput value={form.deductible_basis} options={DEDUCTIBLE_BASIS_OPTIONS}
                           onChange={(v) => update('deductible_basis', v)} />
            </FieldRow>
            <FieldRow label="Deductible Per">
              <SelectInput value={form.deductible_applies_per} options={DEDUCTIBLE_PER_OPTIONS}
                           onChange={(v) => update('deductible_applies_per', v)} />
            </FieldRow>
            <FieldRow label="Net Basis Label">
              <SelectInput value={form.net_basis_label} options={NET_BASIS_OPTIONS}
                           onChange={(v) => update('net_basis_label', v)} />
            </FieldRow>
            <FieldRow label="TCM Fee Rate ($/hr)">
              <NumberInput value={form.tcm_fee_rate} onChange={(v) => update('tcm_fee_rate', v ?? 160)} placeholder="160.00" />
            </FieldRow>
            <FieldRow label="TCM Fee Source">
              <SelectInput value={form.tcm_fee_source} options={FEE_SOURCE_OPTIONS}
                           onChange={(v) => update('tcm_fee_source', v)} />
            </FieldRow>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            {form.loss_run_enabled && (
              <>
                <h3 className="text-sm font-medium text-slate-700 mb-2">Loss Run</h3>
                <FieldRow label="Open Status Filter">
                  <div className="flex gap-4">
                    {STATUS_FILTER_OPTIONS.map((opt) => (
                      <label key={opt.value} className="inline-flex items-center gap-1.5 text-sm">
                        <input
                          type="checkbox"
                          checked={form.open_status_filter.includes(opt.value)}
                          onChange={() => {
                            const next = form.open_status_filter.includes(opt.value)
                              ? form.open_status_filter.filter((v) => v !== opt.value)
                              : [...form.open_status_filter, opt.value];
                            update('open_status_filter', next);
                          }}
                          className="rounded border-slate-300"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </FieldRow>
                <FieldRow label="Group By">
                  <SelectInput value={form.group_by} options={GROUP_BY_OPTIONS} onChange={(v) => update('group_by', v)} />
                </FieldRow>
              </>
            )}
            {form.bdx_enabled && (
              <>
                <h3 className="text-sm font-medium text-slate-700 mb-2 mt-4">BDX</h3>
                <FieldRow label="CORO Split">
                  <Toggle checked={form.coro_split} onChange={(v) => update('coro_split', v)} />
                </FieldRow>
                <FieldRow label="Client Claim # Prefix">
                  <TextInput value={form.client_claim_num_prefix} onChange={(v) => update('client_claim_num_prefix', v)} mono placeholder="e.g. CCL-" />
                </FieldRow>
              </>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-6 pt-4 border-t border-slate-200">
          {step > 0 ? (
            <button onClick={() => setStep(step - 1)} className="text-sm text-slate-600 hover:text-slate-800">
              &larr; Previous
            </button>
          ) : <div />}
          {step < 2 ? (
            <button onClick={() => setStep(step + 1)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
              Next &rarr;
            </button>
          ) : (
            <button onClick={handleCreate} disabled={saving}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:bg-green-300">
              {saving ? 'Creating...' : 'Create Program'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
